import { useState } from "react";
import NewRdv from "../components/NewRdv";
import useOwner from "../../users/hooks/useOwner";
import { useAtomValue } from "jotai";
import {
  aptRequestUsedAtom,
  isStrategorRunningAtom,
  RdvProposal,
  selectedWeekAtom,
  usePreferences,
  useRdvPopupState,
  useSelectedPatientIds,
} from "../../main/state/globalState";
import { useRelevantHoraires } from "../../horaires/hooks/useRelevantHoraires";
import { useThreeWeeksRdvs } from "../queries/rdvsQueries";
import sliceHM from "../processors/sliceHM";
import rdvsFromPlage from "../processors/rdvsFromPlage";
import {
  Collections,
  HMObject,
  typesMotifs,
  CadendarConsts,
  Transforms,
  Appointments,
  HorairesDTO,
  RdvTypeValues,
} from "cadendar-shared";
import { _areDateCompatibleWithConfirmation } from "../filterTransforms/confirmationDates";
import { checkers, formaters } from "vincent-utils";
import _ from "underts";
import useToday from "../../main/hooks/useToday";
import useSelectedPatients from "../../patients/hooks/useSelectedPatients";
import useRdvProposal from "../../ficheAppel/hooks/useRdvProposal";
import { trpc } from "../../main/components/trpc.ts";
import { ExistingPatient } from "cadendar-shared";
import { toast } from "react-toastify";

interface NewRdvApiProps {
  onSave: () => void;
}

const NewRdvApi = (props: NewRdvApiProps) => {
  const owner = useOwner();
  const selectedWeek = useAtomValue(selectedWeekAtom);
  const [horaire] = useRelevantHoraires(selectedWeek);
  const [rdvs, isRdvReady] = useThreeWeeksRdvs(selectedWeek);
  const patients = useSelectedPatients();
  const prefs = usePreferences();

  const { rdvProposal, clearRdvProposal } = useRdvProposal();
  console.log("rdvProposal", rdvProposal);
  // const { rdvCanvas } = useRdvCanvas();
  const { onCloseRdvPopup } = useRdvPopupState();
  // const apiConfig = useAtomValue(ApiConfigState);
  const trpcUtils = trpc.useContext();
  const closeWholeDayMutation = trpc.rdv.closeWholeDay.useMutation({
    onSuccess: () => {
      trpcUtils.rdv.invalidate();
    },
    onError: () => {
      alert(
        "Impossible de fermer la journée, est-ce qu'il ne resterait pas des rdvs présents ?",
      );
    },
  });
  const onCloseWholeDay = async (date: Date) => {
    closeWholeDayMutation.mutate({ date });
    onCloseRdvPopup();
    // const result = await closeWholeDay(apiConfig, apiAuth, date);
    // if (result) {
    //   queryClient.invalidateQueries(["rdvs"]);
    // }
  };
  const reserveForDoctoMutation = trpc.rdv.reserveForDocto.useMutation({
    onSuccess: () => {
      trpcUtils.rdv.invalidate();
    },
    onError: () => {
      alert("Il y a eu une erreur en essayant d'enregistrer");
    },
  });

  const [, { resetSelectedPatientIds }] = useSelectedPatientIds();
  const onReserveForDocto = (options: {
    date: Date;
    duration: number;
    hour: number;
    minute: number;
  }) => {
    console.log("options", JSON.stringify(options, null, 2));
    onCloseRdvPopup();
    resetSelectedPatientIds();

    reserveForDoctoMutation.mutate({ ...options, mode: "manuel" });
  };
  //const commitRdvs = useConfiguratedCommiter(createMultipleRdvs);
  const commitRdvsMutation = trpc.rdv.createMultiple.useMutation({
    onSuccess: (data) => {
      const patient = _.first(patients);
      const rdv = _.first(data);
      clearRdvProposal();
      trpcUtils.rdv.invalidate();
      trpcUtils.aptRequest.invalidate();
      if (!patient) {
        //c'est possible quand on est en train de fermer une plage
        return;
      }
      if (!rdv) {
        throw new Error("no rdv in onSaveRdvs success");
      }
      toast.success(
        formaters.formatNameForenameForDisplay(patient).name +
          " " +
          formaters.formatNameForenameForDisplay(patient).forename +
          " , nous vous confirmons votre rdv le " +
          formaters.formatDateForDisplayWithDay(rdv && rdv.date) +
          " à " +
          formaters.formatHourMinuteObjectForDisplay(rdv) +
          " ",
      );
    },
  });
  const onSaveRdvs = async (options: {
    rdvs: Omit<Appointments, "id" | "log" | "owner">[];
    createReminders: boolean;
    askForConfirmation: boolean;
    mode: "manuel" | "strategor";
    aptRequestId?: string;
  }) => {
    const rdvs = options.rdvs;
    if (!_.isNonEmpty(rdvs)) {
      return;
    }
    const type = _.first(options.rdvs)?.type;
    if (patients.length === 0 && type !== CadendarConsts.typeConsts.FERME) {
      alert("Vous devez sélectionner un patient ou fermer la plage");
      return;
    }

    onCloseRdvPopup();
    props.onSave();
    commitRdvsMutation.mutate({ ...options, rdvs });
  };
  if (prefs && horaire && rdvProposal && owner && isRdvReady) {
    return (
      <NewRdvLogic
        proposal={rdvProposal}
        patients={patients}
        rdvs={rdvs}
        horaire={horaire}
        prefs={prefs}
        owner={owner}
        onSaveRdvs={onSaveRdvs}
        onClosePopup={onCloseRdvPopup}
        onCloseWholeDay={onCloseWholeDay}
        onReserveForDocto={onReserveForDocto}
      />
    );
  }
  return null;
};

const rdvsCreator = (
  rdv: {
    hour: number;
    minute: number;
    date: Date;
    object: string;
    type: RdvTypeValues;
    motifs?: CadendarConsts.MotifsKeys[];
  },
  patients: ExistingPatient[],
  duration: number,
) => {
  console.log("duration in rdvsCreator", duration);
  const step = duration / (patients.length || 1); //patients.length peut être 0 si on ferme la plage
  const HMs = sliceHM(
    { start: rdv, end: new HMObject(rdv).add(duration) },
    step,
  );
  if (patients.length > 0 && HMs.length !== patients.length) {
    throw new Error("HMs.length !== patients.length in rdvsCreator");
  }
  if (!rdv.date) {
    throw new Error("no rdv.date in rdvsCreator");
  }
  //le log et l'_id vont être créés par le serveur
  const rdvsToCreate: Omit<Appointments, "id" | "log" | "owner">[] = HMs.map(
    (hm, index) => {
      const patient = patients[index] || null;
      return {
        date: rdv.date!,
        ...hm,
        object: rdv.object,
        type: rdv.type,
        ...(patient
          ? {
              patient_id: patient.id,
              ...(patient.famille_id
                ? { famille_id: patient.famille_id }
                : null),
            }
          : { patient_id: null }),
        ...(patient?.patient_flags
          ? { patient_flags: patient.patient_flags }
          : {}),
        motif: rdv.motifs?.map((key) => CadendarConsts.motifConsts[key]) || [],
        //TODO: adresseur_id adressedBy
        duration: duration / (patients.length || 1),
        canceled_on: null,
        archived: false,
        confirmation_forced: null,
      };
    },
  );
  return rdvsToCreate;
};

const getHoursPossibles = (
  rdvs: Appointments[],
  dureeMiniRdv: number,
  date: Date,
  horaire: HorairesDTO,
  currentRdv?: Appointments,
) => {
  const bornes = Transforms.horaires.bornesForDay(horaire, date);
  const dayRdvs = rdvs.filter((rdv) => rdv.date.getDate() === date.getDate());
  const rdvsGroupedByPlage = bornes.map((plage: Collections.Plage) =>
    rdvsFromPlage(dayRdvs, plage),
  );
  const emptyCasesGroupedByPlage = rdvsGroupedByPlage.map((plageRdvs, index) =>
    Transforms.rdvs.emptyCases(
      plageRdvs,
      bornes[index],
      dureeMiniRdv,
      currentRdv,
    ),
  );
  return emptyCasesGroupedByPlage.flat(1);
};

const getTypeFromMotif = (motifs: number[], patients: ExistingPatient[]) => {
  if (motifs.length === 0 || patients.length === 0) {
    return 1;
  }
  const patient = _.first(patients)!;
  return typesMotifs.getTypeAndPotentielFromMotifs(motifs, patient).type;
};

const autoSetDuree = (actualDuree: number, dureesPossibles: number[]) => {
  if (dureesPossibles.includes(actualDuree)) {
    return actualDuree;
  } else {
    return _.last(dureesPossibles) || 15;
  }
};

const useConfirmAndReminderAreAuthorized = (
  rdv: { date: Date },
  patients: ExistingPatient[],
) => {
  const today = useToday();
  const patient = _.first(patients);
  const hasReminderTarget =
    !!patient &&
    ((patient.email && checkers.isEmail(patient.email)) ||
      !!patient.portablee164);
  const hasConfirmationTarget = !!patient && !!patient.portablee164;
  const prefs = usePreferences();
  console.log("prefs", JSON.stringify(prefs, null, 2));
  const isSmsTextSet = !!prefs && !!prefs.sms_name && !!prefs.sms_text;
  const isEmailTextSet =
    !!prefs && !!prefs.email_expediteur && !!prefs.email_text;
  const areDateCompatible = _areDateCompatibleWithConfirmation(today, rdv);
  console.log("areDateCompatible", areDateCompatible);
  console.log("today", today);
  console.log("rdv", rdv);
  console.log("hasReminderTarget", hasReminderTarget);
  const isReminderAuthorized =
    hasReminderTarget && isSmsTextSet && isEmailTextSet;
  const isConfirmationAuthorized =
    hasConfirmationTarget && isSmsTextSet && areDateCompatible;
  return { isReminderAuthorized, isConfirmationAuthorized };
};

interface NewRdvLogicProps {
  proposal: RdvProposal;
  patients: ExistingPatient[];
  rdvs: Appointments[];
  horaire: HorairesDTO;
  prefs: { duree_min_rdv: number };
  owner: string;
  onSaveRdvs: (options: {
    rdvs: Omit<Appointments, "id" | "owner">[];
    createReminders: boolean;
    askForConfirmation: boolean;
    mode: "manuel" | "strategor";
    aptRequestId?: string;
  }) => void;
  onClosePopup: () => void;
  onCloseWholeDay: (date: Date) => void;
  onReserveForDocto: (options: {
    date: Date;
    duration: number;
    hour: number;
    minute: number;
  }) => void;
}

const useTempRdv = (
  proposal: RdvProposal,
  patients: ExistingPatient[],
  horaire: HorairesDTO,
  rdvs: Appointments[],
  prefs: { duree_min_rdv: number },
) => {
  const initialDureesPossibles =
    proposal.date && proposal.hour != null && proposal.minute != null
      ? Transforms.rdvs
          .getDurees2(
            horaire,
            rdvs,
            proposal.date,
            {
              hour: proposal.hour,
              minute: proposal.minute,
            },
            prefs.duree_min_rdv,
          )
          .filter(
            (d) => (d / (patients.length || 1)) % prefs.duree_min_rdv === 0,
          )
      : [];

  const [tempRdv, setTempRdv] = useState({
    ...proposal,
    type:
      proposal.type ||
      (getTypeFromMotif(
        proposal.motifs
          ? proposal.motifs.map((key) => CadendarConsts.motifConsts[key])
          : [],
        patients,
      ) as RdvTypeValues),
    reminderChecked: proposal.reminderChecked || false,
    confirmChecked: proposal.confirmChecked || false,
    object: proposal.object || "",
    duration: proposal.duration || _.first(initialDureesPossibles) || 0,
    hour: proposal.hour as number,
    minute: proposal.minute as number,
    date: proposal.date as Date,
  });
  const hmPossibles = proposal.date
    ? getHoursPossibles(rdvs, prefs.duree_min_rdv, proposal.date, horaire)
    : [];

  const dureesPossibles = Transforms.rdvs
    .getDurees2(
      horaire,
      rdvs,
      tempRdv.date,
      { hour: tempRdv.hour, minute: tempRdv.minute },
      prefs.duree_min_rdv,
    )
    .filter((d) => (d / (patients.length || 1)) % prefs.duree_min_rdv === 0);
  const onSetHM = (hm: { hour: number; minute: number }) => {
    setTempRdv((tempRdv) => ({
      ...tempRdv,
      hour: hm.hour,
      minute: hm.minute,
    }));
    const dureesPossibles = tempRdv.date
      ? Transforms.rdvs.getDurees2(
          horaire,
          rdvs,
          tempRdv.date,
          hm,
          prefs.duree_min_rdv,
        )
      : [];
    //on modifie la durée déjà réglée si elle n'est plus possible
    setTempRdv((tempRdv) => {
      const _duree = autoSetDuree(tempRdv.duration, dureesPossibles);
      console.log("set autoSetDuree", _duree);
      return { ...tempRdv, duration: _duree };
    });
  };
  const onSetDuree = (duration: number) => {
    setTempRdv((tempRdv) => ({ ...tempRdv, duration }));
  };
  const onSetType = (type: RdvTypeValues) => {
    setTempRdv((tempRdv) => ({ ...tempRdv, type }));
  };
  const onSetObject = (object: string) => {
    setTempRdv((tempRdv) => ({ ...tempRdv, object }));
  };
  const onToggleReminder = () => {
    setTempRdv((tempRdv) => {
      if (tempRdv.reminderChecked) {
        return {
          ...tempRdv,
          reminderChecked: !tempRdv.reminderChecked,
          confirmChecked: false,
        };
      }
      return {
        ...tempRdv,
        reminderChecked: true,
      };
    });
  };
  const onToggleConfirm = () => {
    setTempRdv((tempRdv) => {
      if (!tempRdv.reminderChecked && !tempRdv.confirmChecked) {
        return {
          ...tempRdv,
          confirmChecked: true,
          reminderChecked: true,
        };
      }
      return {
        ...tempRdv,
        confirmChecked: !tempRdv.confirmChecked,
      };
    });
  };
  const [, { resetSelectedPatientIds }] = useSelectedPatientIds();
  const onClosePlage = () => {
    resetSelectedPatientIds();
    setTempRdv((tempRdv) => ({
      ...tempRdv,
      type: CadendarConsts.typeConsts.FERME,
      patient: null,
      object: "Fermé",
      reminderChecked: false,
      confirmChecked: false,
    }));
  };
  return {
    tempRdv,
    onSetHM,
    onSetDuree,
    onSetType,
    onSetObject,
    onToggleReminder,
    onToggleConfirm,
    onClosePlage,
    dureesPossibles,
    hmPossibles,
  };
};

//dependencies
//rdv du jour
//horaires
//prefs
const NewRdvLogic = (props: NewRdvLogicProps) => {
  const { proposal, patients, rdvs, horaire, prefs } = props;
  const { onClosePopup, onCloseWholeDay, onReserveForDocto } = props;

  const {
    tempRdv,
    onClosePlage,
    onSetObject,
    onSetHM,
    onSetDuree,
    onSetType,
    hmPossibles,
    dureesPossibles,
    onToggleConfirm,
    onToggleReminder,
  } = useTempRdv(proposal, patients, horaire, rdvs, prefs);
  const { isReminderAuthorized, isConfirmationAuthorized } =
    useConfirmAndReminderAreAuthorized(tempRdv, patients);

  console.log("isReminderAuthorized", isReminderAuthorized);

  const isStrategorRunning = useAtomValue(isStrategorRunningAtom);

  const aptRequestUsed = useAtomValue(aptRequestUsedAtom);

  const handleReserveForDocto = () => {
    if (tempRdv.hour == null || tempRdv.minute == null || !tempRdv.duration) {
      alert("Il faut renseigner l'heure et la durée du rendez-vous");
      return;
    }
    onReserveForDocto({
      date: tempRdv.date,
      duration: tempRdv.duration,
      hour: tempRdv.hour,
      minute: tempRdv.minute,
    });
  };
  const onSaveRdvs = () => {
    if (
      patients.length > 0 &&
      (tempRdv.duration! / patients.length) % prefs.duree_min_rdv !== 0
    ) {
      alert(
        `La durée du rendez-vous doit être un multiple de ${prefs.duree_min_rdv} minutes`,
      );
      return;
    }
    _.assertField(tempRdv, "hour");
    const rdvs = rdvsCreator(tempRdv, patients, tempRdv.duration);
    console.log("rdvs to save", JSON.stringify(rdvs, null, 2));
    props.onSaveRdvs({
      rdvs: rdvs,
      createReminders: tempRdv.reminderChecked,
      askForConfirmation: tempRdv.confirmChecked,
      mode: isStrategorRunning ? "strategor" : "manuel",
      ...(aptRequestUsed ? { aptRequestId: aptRequestUsed } : null),
    });
  };
  console.log("tempRdv", tempRdv);

  return (
    <NewRdv
      selectedPatients={props.patients}
      rdvCaracsState={{
        ...tempRdv,
        hour: tempRdv.hour || 0,
        duration: tempRdv.duration || 0,
        hmPossibles,
        dureesPossibles,
        confirmCheckBoxEnabled: isConfirmationAuthorized,
        remindersCheckBoxEnabled: isReminderAuthorized,
      }}
      onJournéeFermée={() => onCloseWholeDay(tempRdv.date)}
      onPlageFermée={onClosePlage}
      dureesPossibles={dureesPossibles}
      hmPossibles={hmPossibles}
      onNewRdvCancel={onClosePopup}
      onNewRdvSave={onSaveRdvs}
      onReserveForDocto={handleReserveForDocto}
      rdvCaracsCallbacks={{
        onDureeChange: onSetDuree,
        onHMChange: onSetHM,
        onTypeChange: onSetType,
        onObjetChange: onSetObject,
        onReminderClick: onToggleReminder,
        onConfirmClick: onToggleConfirm,
      }}
    />
  );
};

export default NewRdvApi;
